<script setup lang="ts">
import { ref, computed, watch, onMounted, useTemplateRef } from "vue";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import { useI18n } from "vue-i18n";
import { ViewGridIcon, ViewListIcon } from "@heroicons/vue/outline";
import { PhotoLayout } from "@/enums/photo-layout";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { usePhotosStore } from "@/stores/modules/photos/photos";
import { usePhotoFilterStore } from "@/stores/modules/photos/photo-filter";
import { useUserStore } from "@/stores/modules/user/user";
import { usePhotoManagement } from "@/composables/usePhotoManagement";
import type { PhotoDocument } from "@kekkle/shared/types/documents/photo-document/photo-document";
import InformationText from "@/components/InformationText.vue";
import Photo from "./Photo.vue";
import PhotoGridItem from "@/components/photos/photo-grid-item.vue";
import AddPhotoBottomSheet from "@/components/AddPhotoBottomSheet.vue";
import EditPhotoBottomSheet from "@/components/EditPhotoBottomSheet.vue";
import PhotoDetailBottomSheet from "@/components/photo-detail-bottom-sheet.vue";
import SkeletonLoader from "../components/ui/SkeletonLoader.vue";
import KekkleProgressBar from "../components/ui/KekkleProgressBar.vue";

const router = useRouter();
const route = useRoute();
const { locale } = useI18n();

const groupsStore = useGroupsStore();
const photosStore = usePhotosStore();
const photoFilterStore = usePhotoFilterStore();
const userStore = useUserStore();

const { addPhotoBottomSheetOpen, editPhotoBottomSheetOpen, selectedPhotoData } =
  usePhotoManagement();

// State
const photoDetailOpen = ref(false);
const detailPhotoData = ref<PhotoDocument | null>(null);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const scrollerRef = useTemplateRef<any>("scroller");
const pendingScrollRestore = ref(
  photosStore.scrollIndex > 0 || photosStore.scrollOffset > 0,
);

// Store references
const photos = computed(() => photosStore.photos);
const loadingInitialPhotos = computed(() => photosStore.loadingInitialPhotos);
const isStale = computed(() => photosStore.isStale);
const fetchError = computed(() => photosStore.fetchError);
const dateFilter = computed(() => photoFilterStore.dateFilter);
const groupData = computed(() => groupsStore.groupData);
const layoutMode = computed(() => photoFilterStore.layoutMode);
const activeGroupId = computed(() => userStore.activeGroup);

// Group photos by month for grid view
type PhotoSection = {
  key: string;
  label: string;
  /** Global index of this section's first photo across all sections. */
  startIndex: number;
  photos: PhotoDocument[];
};

// Number of leading cells that render eagerly (no visibility gate), so the
// initial viewport is populated immediately on navigation.
const EAGER_GRID_COUNT = 20;

const photosByMonth = computed<PhotoSection[]>(() => {
  const sections: PhotoSection[] = [];
  let currentKey = "";
  let processed = 0;

  for (const photo of photos.value) {
    const date = photo.timestamp;
    const key = `${date.getFullYear()}-${date.getMonth()}`;

    if (key !== currentKey) {
      currentKey = key;
      sections.push({
        key,
        label: new Intl.DateTimeFormat(locale.value, {
          year: "numeric",
          month: "long",
        }).format(date),
        startIndex: processed,
        photos: [],
      });
    }
    sections[sections.length - 1]!.photos.push(photo);
    processed++;
  }

  return sections;
});

// Layout toggle
function toggleLayout(): void {
  photoFilterStore.setLayoutMode(
    layoutMode.value === PhotoLayout.List ? PhotoLayout.Grid : PhotoLayout.List,
  );
}

// Grid photo detail
function openPhotoDetail(photo: PhotoDocument): void {
  detailPhotoData.value = photo;
  photoDetailOpen.value = true;
}

function saveScrollState(): void {
  const scroller = scrollerRef.value;
  if (!scroller) return;
  const scrollTop = (scroller.$el as HTMLElement).scrollTop;
  const index = scroller.findItemIndex(scrollTop);
  const itemOffset = scroller.getItemOffset(index);
  photosStore.setScrollState(
    index,
    scrollTop - itemOffset,
    scroller.cacheSnapshot,
  );
}

// Navigation
function goToComments(e: { photoId: string; groupId: string }): void {
  router.push({
    name: "commentPhoto",
    params: { photoId: e.photoId, groupId: e.groupId },
  });
}

// Filters
function clearDateFilter(): void {
  photoFilterStore.setDateFilter(null);

  if (route.query.date) {
    router.replace({ name: "Photos", query: {} });
  }
}

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat(locale.value, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

// Load date filter from URL query if present (for memory features)
photoFilterStore.loadFiltersFromUrl();

// Restore scroll position once a scroller has rendered photos
watch(
  [photos, scrollerRef],
  ([newPhotos, scroller]) => {
    if (!pendingScrollRestore.value || newPhotos.length === 0 || !scroller)
      return;

    pendingScrollRestore.value = false;

    scroller.restoreCache(photosStore.scrollerCache);

    requestAnimationFrame(() => {
      scroller.scrollToItem(photosStore.scrollIndex, {
        offset: photosStore.scrollOffset,
      });
    });
  },
  { immediate: true },
);

onMounted(() => {
  // Subscribe to photos for the active group
  photosStore.subscribeToPhotos();
});

onBeforeRouteLeave(() => {
  saveScrollState();
});
</script>

<template>
  <div class="flex h-screen flex-col pb-16">
    <h1
      class="w-full max-w-xl shrink-0 border-b-2 border-b-kekkle-purple bg-white py-4 text-center text-xl font-medium dark:bg-kekkle-dark-dark dark:text-slate-200"
    >
      {{ $t("pages.photo.title_of") }}
      {{ groupData?.name ?? "" }}
    </h1>
    <KekkleProgressBar :active="isStale" color="purple" />
    <div class="my-2 shrink-0 px-2">
      <InformationText
        v-if="fetchError && !loadingInitialPhotos"
        color="red"
        class="mx-auto"
        :information="fetchError"
      />

      <InformationText
        v-if="!fetchError && dateFilter && photos.length > 0"
        dismissable
        :information="$t('pages.photos.showing_photos_from')"
        @dismiss="clearDateFilter"
      >
        {{ formatDate(dateFilter) }}
      </InformationText>

      <InformationText
        v-if="
          !fetchError &&
          dateFilter &&
          photos.length === 0 &&
          !loadingInitialPhotos
        "
        dismissable
        class="mx-auto"
        :information="$t('pages.photos.no_photos_this_date')"
        @dismiss="clearDateFilter"
      >
        {{ formatDate(dateFilter) }}
      </InformationText>

      <InformationText
        v-if="
          !fetchError &&
          !dateFilter &&
          photos.length === 0 &&
          !loadingInitialPhotos
        "
        class="mx-auto"
        :information="$t('pages.photo.no_photos_yet')"
      />
    </div>

    <!-- Action Bar -->
    <div
      v-if="photos.length > 0"
      class="mb-2 flex w-full shrink-0 items-center justify-center gap-2 px-2"
    >
      <button
        class="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-black-dark bg-white transition-all hover:shadow-purple-hard-sm active:shadow-none dark:bg-kekkle-dark-light"
        :aria-label="
          layoutMode === PhotoLayout.List
            ? $t('pages.photos.grid_view')
            : $t('pages.photos.list_view')
        "
        @click="toggleLayout"
      >
        <ViewGridIcon
          v-if="layoutMode === PhotoLayout.List"
          class="h-5 w-5 text-kekkle-purple"
        />
        <ViewListIcon v-else class="h-5 w-5 text-kekkle-purple" />
      </button>
    </div>

    <div v-if="loadingInitialPhotos" class="m-4">
      <SkeletonLoader></SkeletonLoader>
    </div>

    <!-- List view with virtual scroller -->
    <DynamicScroller
      v-if="layoutMode === PhotoLayout.List"
      ref="scroller"
      :items="photos"
      :min-item-size="300"
      :buffer="600"
      key-field="_id"
      class="min-h-0 flex-1 px-2"
    >
      <template #default="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :data-index="index"
          :size-dependencies="[
            (item as any).caption,
            (item as any).reported_by,
          ]"
        >
          <div class="pb-2">
            <Photo :photo-doc="item" @go-to-comments="goToComments" />
          </div>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>
    <!-- <div
      v-if="layoutMode === PhotoLayout.List"
      class="min-h-0 flex-1 overflow-y-scroll px-2"
    >
      <Photo
        v-for="item in photos"
        :key="item._id"
        :photo-doc="item"
        @go-to-comments="goToComments"
      ></Photo>
    </div> -->

    <!-- Grid view -->
    <div v-else class="min-h-0 flex-1 overflow-y-auto px-2">
      <div v-for="section in photosByMonth" :key="section.key" class="mb-4">
        <h2
          class="mb-1.5 text-sm font-semibold capitalize text-gray-500 dark:text-slate-400"
        >
          {{ section.label }}
        </h2>
        <div class="grid grid-cols-3 gap-1">
          <PhotoGridItem
            v-for="(photo, photoIndex) in section.photos"
            :key="photo._id"
            :photo-doc="photo"
            :group-id="activeGroupId ?? ''"
            :eager="section.startIndex + photoIndex < EAGER_GRID_COUNT"
            @click="openPhotoDetail"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Bottom Sheets -->
  <AddPhotoBottomSheet v-model:open="addPhotoBottomSheetOpen" />
  <EditPhotoBottomSheet
    v-model:open="editPhotoBottomSheetOpen"
    :photo-data="selectedPhotoData"
  />
  <PhotoDetailBottomSheet
    v-model:open="photoDetailOpen"
    :photo-data="detailPhotoData"
    @go-to-comments="goToComments"
  />
</template>
